import { useEffect, useCallback, useRef } from "react";
import { useBlocker, useNavigate } from "react-router";

const defaultConfirmLeave = (msg) => window.confirm(msg);

function locationsDiffer(currentLocation, nextLocation) {
	return (
		currentLocation.pathname !== nextLocation.pathname ||
		currentLocation.search !== nextLocation.search ||
		currentLocation.hash !== nextLocation.hash
	);
}

/**
 * Блокировка ухода со страницы при несохранённых данных.
 * Работает только при data router (createBrowserRouter + RouterProvider).
 *
 * @param {boolean} shouldBlock — блокировать переходы (например form dirty)
 * @param {object} [options]
 * @param {string} [options.message] — текст confirm и подсказка при закрытии вкладки
 * @param {(msg: string) => boolean} [options.confirmLeave] — своя модалка вместо window.confirm
 * @returns {(to: string | number, options?: object) => void} safeNavigate — как navigate, но с подтверждением
 */
export function useUnsavedChangesGuard(shouldBlock, options = {}) {
	const {
		message = "Данные будут потеряны без сохранения. Выйти?",
		confirmLeave: confirmLeaveOption,
	} = options;

	const confirmLeave = confirmLeaveOption ?? defaultConfirmLeave;
	const confirmLeaveRef = useRef(confirmLeave);
	confirmLeaveRef.current = confirmLeave;

	const navigate = useNavigate();
	const shouldBlockRef = useRef(shouldBlock);
	shouldBlockRef.current = shouldBlock;

	const blocker = useBlocker(
		useCallback(
			({ currentLocation, nextLocation }) =>
				shouldBlockRef.current &&
				locationsDiffer(currentLocation, nextLocation),
			[]
		)
	);

	const blockerRef = useRef(blocker);
	blockerRef.current = blocker;

	useEffect(() => {
		if (blocker.state !== "blocked") return;
		const ok = confirmLeaveRef.current(message);
		if (ok) blockerRef.current.proceed();
		else blockerRef.current.reset();
	}, [blocker.state, message]);

	useEffect(() => {
		const onBeforeUnload = (event) => {
			if (!shouldBlockRef.current) return;
			event.preventDefault();
			event.returnValue = message;
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [message]);

	const safeNavigate = useCallback(
		(to, opts) => {
			if (!shouldBlockRef.current || confirmLeaveRef.current(message)) {
				navigate(to, opts);
			}
		},
		[navigate, message]
	);

	return safeNavigate;
}

export default useUnsavedChangesGuard;
